import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Nav';
import { Switch } from '@headlessui/react';
import toast, { Toaster } from 'react-hot-toast';
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();


export default function AdminSettingsPage() {
  const [users, setUsers] = useState([]);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({
  email: '',
  password: '',
  role: '',
  admin: false,
  dash_access: false,
  activity_log_access: false
});
  const [registerError, setRegisterError] = useState('');
  const [editPasswordConfirm, setEditPasswordConfirm] = useState('');
  const [editPasswordStrength, setEditPasswordStrength] = useState(0);
  const [registerPasswordStrength, setRegisterPasswordStrength] = useState(0);

  const token = localStorage.getItem('auth_token');

  const getPasswordTips = (password) => {
    return [
      { label: 'At least 8 characters', valid: password.length >= 8 },
      { label: 'One uppercase letter', valid: /[A-Z]/.test(password) },
      { label: 'One number', valid: /[0-9]/.test(password) },
      { label: 'One special character', valid: /[^A-Za-z0-9]/.test(password) },
    ];
  };

  const renderPasswordTips = (password) => (
    <ul className="text-sm text-gray-600 space-y-1 pl-4">
      {getPasswordTips(password).map((tip, idx) => (
        <li key={idx} className={`flex items-center gap-2 ${tip.valid ? 'text-green-600' : 'text-red-500'}`}>
          {tip.valid ? '✔️' : '❌'} {tip.label}
        </li>
      ))}
    </ul>
  );

  const fetchUsers = () => {
    fetch(`${API_BASE_URL}/users`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setUsers(data.users || []));
  };

  useEffect(() => {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.admin === true) {
      setIsSuperUser(true);
      fetchUsers();
    }
  }, [token]);

  useEffect(() => {
    document.title = "Admin Settings | IP Intelligence Portal";
  }, []);

  const post = async (endpoint, body) => {
    const res = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Something went wrong');
    await fetchUsers();
    return data;
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const renderStrengthBar = (strength) => {
    const levels = ["bg-red-500", "bg-yellow-500", "bg-blue-500", "bg-green-600"];
    const labels = ["Weak", "Medium", "Good", "Strong"];
    return (
      <div className="w-full mt-1">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Password Strength</label>
        <div className="w-full h-2 bg-gray-200 rounded">
          <div className={`h-2 ${levels[strength - 1]} rounded transition-all duration-300`} style={{ width: `${(strength / 4) * 100}%` }}></div>
        </div>
        {strength > 0 && <div className="text-xs mt-1 text-gray-600">{labels[strength - 1]}</div>}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-10 p-6 text-gray-800">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Admin Settings</h2>
          {isSuperUser && (
            <button
              onClick={() => {
                setShowRegisterModal(true);
                setRegisterError('');
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow"
            >
              + New User
            </button>
          )}
        </div>

        <div className="bg-white border p-6 rounded-lg shadow">
          <h3 className="font-semibold text-lg mb-4">User List</h3>
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-center">Role</th>
                <th className="p-3 text-center">Admin</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.email} className="border-t hover:bg-gray-50">
                  <td className="p-3">{user.email}</td>
                  <td className="p-3 text-center">{user.role}</td>
                  <td className="p-3 text-center">{user.admin ? '✅' : '❌'}</td>
                  <td className="p-3 text-center space-x-2">
                    <button
                      onClick={() => {
                        setEditUser({
                          ...user,
                          new_email: user.email,
                          new_password: '',
                          dash_access: user.dash_access,
                          activity_log_access: user.activity_log_access,
                        });
                        setEditPasswordConfirm('');
                        setEditPasswordStrength(0);
                      }}                      
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                    >
                      Edit
                    </button>
                    {user.email !== JSON.parse(atob(token.split('.')[1])).email && (
                      <button
                        onClick={() => setDeleteUser(user)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showRegisterModal && (
  <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
    <div className="bg-white w-full max-w-2xl p-8 rounded-lg shadow-lg">
      <h3 className="text-2xl font-semibold mb-6">Register New User</h3>
      <div className="flex flex-col space-y-4">
        <input
          placeholder="Email"
          className="border rounded px-4 py-2"
          value={registerForm.email}
          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
        />

        <input
          placeholder="Password"
          type="password"
          className="border rounded px-4 py-2"
          value={registerForm.password}
          onChange={(e) => {
            const val = e.target.value;
            setRegisterForm({ ...registerForm, password: val });
            setRegisterPasswordStrength(getPasswordStrength(val));
          }}
        />

        {registerForm.password && (
          <>
            <div className="mt-2">{renderStrengthBar(registerPasswordStrength)}</div>
            {renderPasswordTips(registerForm.password)}
          </>
        )}

        <input
          placeholder="Confirm Password"
          type="password"
          className={`border rounded px-4 py-2 ${registerForm.confirmPassword && registerForm.password !== registerForm.confirmPassword ? 'border-red-500' : ''}`}
          value={registerForm.confirmPassword}
          onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
        />
        {registerForm.confirmPassword && registerForm.password !== registerForm.confirmPassword && (
          <p className="text-red-500 text-sm -mt-2">Passwords do not match.</p>
        )}

        <input
          placeholder="Role (e.g. ISP, LEA)"
          className="border rounded px-4 py-2"
          value={registerForm.role}
          onChange={(e) => setRegisterForm({ ...registerForm, role: e.target.value })}
        />

        <div className="flex items-center justify-between pt-3">
          <label htmlFor="admin-switch" className="text-sm font-medium text-gray-700">Admin Access</label>
          <Switch
            checked={registerForm.admin}
            onChange={(val) => setRegisterForm({ ...registerForm, admin: val })}
            className={`${registerForm.admin ? 'bg-green-600' : 'bg-gray-300'}
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            id="admin-switch"
          >
            <span
              className={`${registerForm.admin ? 'translate-x-6' : 'translate-x-1'}
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
      </div>
      <div className="flex items-center justify-between">
    <label htmlFor="dash-switch" className="text-sm font-medium text-gray-700">Dashboard Access</label>
    <Switch
      checked={registerForm.dash_access}
      onChange={(val) => setRegisterForm({ ...registerForm, dash_access: val })}
      className={`${registerForm.dash_access ? 'bg-green-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full`}
      id="dash-switch"
    >
      <span className={`${registerForm.dash_access ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white`} />
    </Switch>
  </div>

  <div className="flex items-center justify-between">
    <label htmlFor="activity-switch" className="text-sm font-medium text-gray-700">Activity Log Access</label>
    <Switch
      checked={registerForm.activity_log_access}
      onChange={(val) => setRegisterForm({ ...registerForm, activity_log_access: val })}
      className={`${registerForm.activity_log_access ? 'bg-green-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full`}
      id="activity-switch"
    >
      <span className={`${registerForm.activity_log_access ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white`} />
    </Switch>
  </div>


      {registerError && (
        <div className="mt-4 text-sm text-red-600 font-medium">{registerError}</div>
      )}

      <div className="flex justify-end space-x-2 mt-6">
        <button
          onClick={() => {
            setShowRegisterModal(false);
            setRegisterForm({ email: '', password: '', confirmPassword: '', role: '', admin: false });
            setRegisterError('');
            setRegisterPasswordStrength(0);
          }}
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            setRegisterError('');
            if (!registerForm.email || !registerForm.password || !registerForm.confirmPassword || !registerForm.role) {
              setRegisterError("All fields are required.");
              return;
            }
            if (registerForm.password !== registerForm.confirmPassword) {
              setRegisterError("Passwords do not match.");
              return;
            }
            if (registerPasswordStrength < 3) {
              setRegisterError("Password is too weak. Use at least 8 characters, uppercase, number, and symbol.");
              return;
            }
            try {
                await post('register', {
                    email: registerForm.email,
                    password: registerForm.password,
                    role: registerForm.role,
                    admin: registerForm.admin,
                    dash_access: registerForm.dash_access,
                    activity_log_access: registerForm.activity_log_access
                  });
              toast.success("User created successfully!");
              setShowRegisterModal(false);
              setRegisterForm({ email: '', password: '', confirmPassword: '', role: '', admin: false });
              setRegisterPasswordStrength(0);
            } catch (err) {
              setRegisterError(err.message);
            }
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
        >
          Create User
        </button>
      </div>
    </div>
  </div>
)}


{editUser && (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
    <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-lg">
      <h3 className="text-lg font-bold mb-4">Edit User</h3>

      <div className="flex flex-col space-y-4">
        <input
          className="w-full border px-4 py-2 rounded"
          placeholder="New Email"
          value={editUser.new_email || editUser.email}
          onChange={(e) => setEditUser({ ...editUser, new_email: e.target.value })}
        />

        <input
          className="w-full border px-4 py-2 rounded"
          placeholder="New Password"
          type="password"
          onChange={(e) => {
            const val = e.target.value;
            setEditUser({ ...editUser, new_password: val });
            setEditPasswordStrength(getPasswordStrength(val));
          }}
        />

        {editUser.new_password && (
          <>
            <div className="mt-1">{renderStrengthBar(editPasswordStrength)}</div>
            <ul className="text-sm text-gray-600 space-y-1 pl-4">
              {getPasswordTips(editUser.new_password).map((tip, idx) => (
                <li key={idx} className={`flex items-center gap-2 ${tip.valid ? 'text-green-600' : 'text-red-500'}`}>
                  {tip.valid ? '✔️' : '❌'} {tip.label}
                </li>
              ))}
            </ul>
          </>
        )}

        <input
          className={`w-full border px-4 py-2 rounded ${
            editUser.new_password &&
            editPasswordConfirm &&
            editUser.new_password !== editPasswordConfirm
              ? 'border-red-500'
              : ''
          }`}
          placeholder="Confirm Password"
          type="password"
          value={editPasswordConfirm}
          onChange={(e) => setEditPasswordConfirm(e.target.value)}
        />
        {editUser.new_password &&
          editPasswordConfirm &&
          editUser.new_password !== editPasswordConfirm && (
            <p className="text-red-500 text-sm -mt-2">Passwords do not match.</p>
          )}

        <input
          className="w-full border px-4 py-2 rounded"
          placeholder="Role"
          value={editUser.role}
          onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
        />

        <div className="flex items-center justify-between pt-2">
          <label htmlFor="admin-switch-edit" className="text-sm font-medium text-gray-700">Admin Access</label>
          <Switch
            checked={editUser.admin}
            onChange={(val) => setEditUser({ ...editUser, admin: val })}
            className={`${editUser.admin ? 'bg-green-600' : 'bg-gray-300'}
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            id="admin-switch-edit"
          >
            <span
              className={`${editUser.admin ? 'translate-x-6' : 'translate-x-1'}
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
        <div className="flex items-center justify-between">
    <label className="text-sm font-medium text-gray-700">Dashboard Access</label>
    <Switch
      checked={editUser.dash_access}
      onChange={(val) => setEditUser({ ...editUser, dash_access: val })}
      className={`${editUser.dash_access ? 'bg-green-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full`}
    >
      <span className={`${editUser.dash_access ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white`} />
    </Switch>
  </div>

  <div className="flex items-center justify-between">
    <label className="text-sm font-medium text-gray-700">Activity Log Access</label>
    <Switch
      checked={editUser.activity_log_access}
      onChange={(val) => setEditUser({ ...editUser, activity_log_access: val })}
      className={`${editUser.activity_log_access ? 'bg-green-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full`}
    >
      <span className={`${editUser.activity_log_access ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white`} />
    </Switch>
  </div>
      </div>

      <div className="flex justify-end space-x-2 mt-6">
        <button
          onClick={() => setEditUser(null)}
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            await post('edit-user', {
                email: editUser.email,
                new_email: editUser.new_email,
                new_password: editUser.new_password,
                role: editUser.role,
                admin: editUser.admin,
                dash_access: editUser.dash_access,
                activity_log_access: editUser.activity_log_access,
              });
              setEditUser(null);
              toast.success("User updated successfully!");              
            await post('edit-user', {
              email: editUser.email,
              new_email: editUser.new_email,
              new_password: editUser.new_password,
              role: editUser.role,
              admin: editUser.admin,
              dash_access: editUser.dash_access,
              activity_log_access: editUser.activity_log_access,
            });
            setEditUser(null);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
)}



        {deleteUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
            <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg text-center">
              <h3 className="text-lg font-bold mb-4 text-red-600">Confirm Delete</h3>
              <p className="text-sm mb-6">
                Are you sure you want to delete <strong>{deleteUser.email}</strong>?
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setDeleteUser(null)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await post('delete-user', { email: deleteUser.email });
                    setDeleteUser(null);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
